import { type KeyboardEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { type Document, type DocumentSortBy } from "@/hooks/use-document-repo";
import { formatTimestampDate } from "@/lib/dates";
import { formatBytes, mimeLabel, mimeColor } from "@/lib/file-format";
import { isProtected, canInlineDownload } from "@/lib/document-source";
import { EyeIcon, DownloadIcon, TrashIcon, LockClosedIcon } from "@/components/icons";
import { linkedRecordLabel } from "@/components/violations/linked-record-label";
import { DocumentSourceBadge } from "./document-source-badge";

interface DocumentRepoTableProps {
  docs: Document[];
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  onPreview: (doc: Document) => void;
  onDownload: (doc: Document) => void;
  onDelete: (doc: Document) => void;
}

/** Sortable columns map to the server allow-list keys; the rest are not. */
const SORTABLE: Record<string, DocumentSortBy> = {
  filename: "filename",
  source: "source",
  created_at: "created_at",
  size_bytes: "size_bytes",
};

interface HeaderSpec {
  id: string;
  labelKey: string;
  align?: "left" | "right";
  className?: string;
}

const HEADERS: HeaderSpec[] = [
  { id: "filename", labelKey: "repo.columns.name" },
  { id: "source", labelKey: "repo.columns.source", className: "w-[1%] whitespace-nowrap" },
  { id: "context_label", labelKey: "repo.columns.attachedTo" },
  {
    id: "uploaded_by_name",
    labelKey: "repo.columns.uploadedBy",
    className: "w-[1%] whitespace-nowrap",
  },
  { id: "created_at", labelKey: "repo.columns.date", className: "w-[1%] whitespace-nowrap" },
  { id: "size_bytes", labelKey: "repo.columns.size", className: "w-[1%] whitespace-nowrap" },
  { id: "actions", labelKey: "", align: "right", className: "w-[1%]" },
];

/**
 * The Drive-density document table (mockup 01 right pane). Flush to the card
 * edge — no inner padding wrapper — with `9px 14px` cells, a 24px file-type
 * icon chip, a one-line source chip, a two-tone "Attached to" context column,
 * muted uploader/date/size, and a 3-button row-action cluster (preview / eye,
 * download, delete / lock-protected). Server-paged + server-sorted: a
 * sortable header click flips the single active server sort key via
 * onSortingChange (NO NAKED TABLES — every data column declares its sort
 * behavior here, mirrored by the columns factory the colocated test pins).
 */
export function DocumentRepoTable({
  docs,
  sorting,
  onSortingChange,
  onPreview,
  onDownload,
  onDelete,
}: DocumentRepoTableProps) {
  const { t } = useTranslation(["documents", "violations"]);
  const active = sorting[0];

  const toggleSort = (id: string) => {
    const key = SORTABLE[id];
    if (!key) return;
    if (active?.id === id) {
      onSortingChange([{ id, desc: !active.desc }]);
    } else {
      onSortingChange([{ id, desc: false }]);
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {HEADERS.map((h) => {
              const sortable = Boolean(SORTABLE[h.id]);
              const isActive = active?.id === h.id;
              const ariaSort: "ascending" | "descending" | undefined = isActive
                ? active.desc
                  ? "descending"
                  : "ascending"
                : undefined;
              return (
                <th
                  key={h.id}
                  scope="col"
                  aria-sort={ariaSort}
                  role={sortable ? "button" : undefined}
                  tabIndex={sortable ? 0 : undefined}
                  onClick={sortable ? () => toggleSort(h.id) : undefined}
                  onKeyDown={
                    sortable
                      ? (e: KeyboardEvent<HTMLTableCellElement>) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleSort(h.id);
                          }
                        }
                      : undefined
                  }
                  className={`border-b border-gray-100 px-3.5 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:border-gray-700 dark:text-gray-500 ${
                    h.align === "right" ? "text-right" : "text-left"
                  } ${sortable ? "cursor-pointer select-none hover:text-gray-600 dark:hover:text-gray-300" : ""} ${h.className ?? ""}`}
                >
                  <span
                    className={`inline-flex items-center gap-1 ${h.align === "right" ? "justify-end" : ""}`}
                  >
                    {h.labelKey ? t(h.labelKey) : ""}
                    {sortable && (
                      <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">
                        {isActive ? (active.desc ? "↓" : "↑") : "↕"}
                      </span>
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {docs.map((doc) => {
            const label = mimeLabel(doc.content_type);
            const context = linkedRecordLabel(t, doc.context_type, doc.context_label);
            const protectedDoc = isProtected(doc);
            const downloadable = canInlineDownload(doc.source);
            return (
              <tr
                key={`${doc.source}:${doc.id}`}
                onClick={() => onPreview(doc)}
                className="cursor-pointer border-b border-gray-50 transition-colors last:border-b-0 hover:bg-gray-50 dark:border-gray-700/60 dark:hover:bg-gray-700/40"
              >
                {/* Name + file-type icon */}
                <td className="px-3.5 py-2.5 align-middle">
                  <div className="flex items-center gap-2.5">
                    <span
                      className={`flex h-6 w-6 flex-none items-center justify-center rounded-md text-[9px] font-bold text-white ${mimeColor(label)}`}
                      aria-hidden="true"
                    >
                      {label}
                    </span>
                    <span className="truncate text-[12.5px] font-semibold text-on-surface">
                      {doc.filename}
                    </span>
                  </div>
                </td>
                {/* Source chip */}
                <td className="whitespace-nowrap px-3.5 py-2.5 align-middle">
                  <DocumentSourceBadge source={doc.source} />
                </td>
                {/* Attached to (two-tone context) — one line, ellipsis + hover title */}
                <td className="max-w-[220px] px-3.5 py-2.5 align-middle text-[12.5px] text-on-surface">
                  {context ? (
                    <span className="block truncate whitespace-nowrap" title={context}>
                      {context}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">
                      {t("repo.columns.unfiled")}
                    </span>
                  )}
                </td>
                {/* Uploaded by */}
                <td className="whitespace-nowrap px-3.5 py-2.5 align-middle text-[12.5px] text-gray-400 dark:text-gray-500">
                  {doc.uploaded_by_name || "—"}
                </td>
                {/* Date */}
                <td className="whitespace-nowrap px-3.5 py-2.5 align-middle text-[12.5px] text-gray-500 dark:text-gray-400">
                  {formatTimestampDate(doc.created_at)}
                </td>
                {/* Size */}
                <td className="whitespace-nowrap px-3.5 py-2.5 align-middle text-[12.5px] text-gray-500 dark:text-gray-400">
                  {formatBytes(doc.size_bytes)}
                </td>
                {/* Row actions */}
                <td className="px-3.5 py-2.5 align-middle">
                  <div
                    className="flex items-center justify-end gap-1"
                    onClick={(e) => e.stopPropagation()}
                    role="presentation"
                  >
                    <RowAction
                      label={t("repo.actions.preview")}
                      onClick={() => onPreview(doc)}
                      icon={<EyeIcon className="h-3.5 w-3.5" />}
                    />
                    <RowAction
                      label={
                        downloadable
                          ? t("repo.actions.download")
                          : t("repo.actions.downloadAtSource")
                      }
                      ariaLabel={t("repo.actions.download")}
                      disabled={!downloadable}
                      onClick={() => onDownload(doc)}
                      icon={<DownloadIcon className="h-3.5 w-3.5" />}
                    />
                    <RowAction
                      label={
                        protectedDoc ? t("repo.actions.protectedTooltip") : t("repo.actions.delete")
                      }
                      ariaLabel={t("repo.actions.delete")}
                      disabled={protectedDoc}
                      danger
                      onClick={() => onDelete(doc)}
                      icon={
                        protectedDoc ? (
                          <LockClosedIcon className="h-3.5 w-3.5" />
                        ) : (
                          <TrashIcon className="h-3.5 w-3.5" />
                        )
                      }
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface RowActionProps {
  label: string;
  ariaLabel?: string;
  icon: ReactNode;
  disabled?: boolean;
  danger?: boolean;
  onClick: () => void;
}

/** A 26px bordered icon button matching the mockup `.ib` row-action chip. */
function RowAction({ label, ariaLabel, icon, disabled, danger, onClick }: RowActionProps) {
  return (
    <button
      type="button"
      title={label}
      aria-label={ariaLabel ?? label}
      disabled={disabled}
      onClick={onClick}
      className={`flex h-[26px] w-[26px] items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 transition-colors disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 ${
        danger
          ? "hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20 dark:hover:text-red-400"
          : "hover:bg-gray-100 hover:text-on-surface dark:hover:bg-gray-700 dark:hover:text-gray-200"
      }`}
    >
      {icon}
    </button>
  );
}
